import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/cart-shopping-carts/my-cart/add';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type AddProductToCartInput = paths[url]['post']['requestBody']['content']['application/json'];
export type AddProductToCartSuccessResponse = paths[url]['post']['responses']['200']['content']['application/json'];

/**
 * Requests endpoint to add product to shopping cart.
 */
export const useAddProductToCart = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setCart } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [lastSuccessfulResponseTime, setLastSuccessfulResponseTime] = useState<Date | null>(null);
      const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const sessionId = getSessionId();

    const request = async (props: AddProductToCartInput) => {
        setLoading(true);
        setErrorMessage(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props, session_id: sessionId }),
            });

             if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } catch (error: any) {
                    error.message = '';
                }
                
                error.statusCode = response.status;
                
                throw error;
            }

            const data: AddProductToCartSuccessResponse = await response.json();

            setCart(data);
            setLastSuccessfulResponseTime(new Date());
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setErrorMessage(error.message);
        } finally {
            setLoading(false);
        }
    };

    return { request, loading, lastSuccessfulResponseTime, errorMessage };
};
